import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../context/ProfileContext';
import { Funnel, ArrowRight, ShieldSlash, ShieldCheck, Sparkle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Must-Contain Gate Stats — per actief profiel.
 *
 * Toont in één oogopslag of de must_contain pre-save gate vervuiling tegenhoudt:
 *  - Aantal geweigerde facilities (skip_must_contain)
 *  - Aantal doorgelaten facilities (facilities_found)
 *  - Skip-rate %
 *  - Mini sparkline van afgelopen 7 dagen
 *  - Actief geconfigureerde must_contain woorden
 *  - Hint indien rate extreem hoog (>80%) of laag (0%) → woorden bijstellen
 */
export default function MustContainStatsWidget() {
  const navigate = useNavigate();
  const { active } = useProfile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/scans/must-contain-stats?days=7`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (!cancelled) setData(j); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active]);

  const hint = useMemo(() => {
    if (!data || !data.gate_active) return null;
    const candidates = (data.total_skipped || 0) + (data.total_found || 0);
    if (candidates < 20) return { tone: 'neutral', text: 'Te weinig data om relevantie-woorden te beoordelen.' };
    if (data.skip_rate > 80) return { tone: 'warn', text: 'Skip-rate erg hoog (>80%). Mogelijk te streng — overweeg synoniemen toe te voegen.' };
    if (data.skip_rate === 0) return { tone: 'warn', text: 'Geen enkele facility geweigerd — gate doet effectief niks. Check de woorden.' };
    if (data.skip_rate < 5) return { tone: 'ok', text: 'Lage skip-rate — bron is al schoon, gate werkt als safety net.' };
    return { tone: 'ok', text: 'Skip-rate gezond — gate houdt off-topic data buiten je database.' };
  }, [data]);

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 p-5" data-testid="must-contain-widget-loading">
        <div className="flex items-center gap-2 text-zinc-400">
          <div className="w-3 h-3 border-2 border-zinc-300 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs">Laden…</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const candidates = (data.total_skipped || 0) + (data.total_found || 0);
  const skipRate = data.skip_rate || 0;
  const maxDay = Math.max(1, ...(data.by_day || []).map(d => d.skipped + d.found));
  const gateActive = !!data.gate_active;

  return (
    <div className="bg-white border border-zinc-200" data-testid="must-contain-widget">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <Funnel size={16} weight="bold" className="text-violet-600" />
          <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-zinc-700">
            Relevantie-gate · laatste {data.days} dagen
          </h3>
        </div>
        <button
          data-testid="must-contain-widget-edit-terms"
          onClick={() => navigate('/search-terms-v2')}
          className="text-xs font-medium text-[#0055FF] hover:underline flex items-center gap-1"
        >
          Beheer woorden <ArrowRight size={12} />
        </button>
      </div>

      {!gateActive ? (
        <div className="px-5 py-6 flex items-start gap-3">
          <ShieldSlash size={20} weight="bold" className="text-amber-500 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-zinc-800 mb-1">Geen relevantie-woorden ingesteld</div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              Voor profiel <strong>{data.profile_id}</strong> zijn nog geen <code className="px-1 py-0.5 bg-zinc-100 text-[10px]">must_contain</code> woorden geconfigureerd.
              Hierdoor mag de scanner elke faciliteit opslaan — ook off-topic resultaten zoals industriële bedrijven met overlappende namen.
            </p>
            <button
              data-testid="must-contain-widget-configure"
              onClick={() => navigate('/search-terms-v2')}
              className="mt-3 text-xs font-medium px-3 py-1.5 bg-violet-600 text-white hover:bg-violet-700 transition-colors"
            >
              Stel relevantie-woorden in
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-100">
          {/* KPIs */}
          <div className="bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={14} weight="bold" className="text-emerald-600" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Doorgelaten</span>
            </div>
            <div className="font-data text-2xl font-bold text-emerald-700" data-testid="mc-total-found">{data.total_found}</div>
            <div className="text-[11px] text-zinc-500 mt-1">facilities opgeslagen</div>
          </div>
          <div className="bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldSlash size={14} weight="bold" className="text-rose-600" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Geweigerd</span>
            </div>
            <div className="font-data text-2xl font-bold text-rose-700" data-testid="mc-total-skipped">{data.total_skipped}</div>
            <div className="text-[11px] text-zinc-500 mt-1">off-topic geblokkeerd</div>
          </div>
          <div className="bg-white p-5">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={14} weight="bold" className="text-violet-600" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Skip-rate</span>
            </div>
            <div className="font-data text-2xl font-bold text-violet-700" data-testid="mc-skip-rate">{skipRate}%</div>
            <div className="text-[11px] text-zinc-500 mt-1">
              {candidates > 0 ? `${data.total_skipped} van ${candidates} kandidaten` : 'geen scans in venster'}
            </div>
          </div>
        </div>
      )}

      {gateActive && (
        <>
          {/* Sparkline */}
          {(data.by_day || []).length > 0 && (
            <div className="px-5 py-4 border-t border-zinc-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Per dag</span>
                <span className="text-[10px] text-zinc-400">{data.total_scans} scans totaal</span>
              </div>
              <div className="flex items-end gap-1 h-16" data-testid="mc-sparkline">
                {data.by_day.map(d => {
                  const total = d.skipped + d.found;
                  const skipH = total > 0 ? (d.skipped / maxDay) * 100 : 0;
                  const foundH = total > 0 ? (d.found / maxDay) * 100 : 0;
                  return (
                    <div key={d.day} className="flex-1 flex flex-col justify-end" title={`${d.day}: ${d.skipped} geweigerd / ${d.found} doorgelaten`}>
                      <div className="bg-rose-400" style={{ height: `${skipH}%` }} />
                      <div className="bg-emerald-400" style={{ height: `${foundH}%` }} />
                      <div className="text-[9px] text-zinc-400 text-center mt-1 font-data">{d.day.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500 mt-2">
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-rose-400" /> geweigerd</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 bg-emerald-400" /> doorgelaten</span>
              </div>
            </div>
          )}

          {/* Actieve woorden + hint */}
          <div className="px-5 py-4 border-t border-zinc-100 flex flex-col gap-3">
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 block mb-2">
                Actieve relevantie-woorden ({data.must_contain_words.length})
              </span>
              <div className="flex flex-wrap gap-1">
                {data.must_contain_words.slice(0, 20).map(w => (
                  <span key={w} className="inline-block text-[11px] px-2 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 font-data" data-testid={`mc-word-${w}`}>
                    {w}
                  </span>
                ))}
                {data.must_contain_words.length > 20 && (
                  <span className="text-[11px] text-zinc-400 font-data px-2 py-0.5">+{data.must_contain_words.length - 20} meer</span>
                )}
              </div>
            </div>
            {hint && (
              <div
                data-testid="mc-hint"
                className={`text-xs px-3 py-2 border-l-2 ${
                  hint.tone === 'warn' ? 'bg-amber-50 border-amber-500 text-amber-800' :
                  hint.tone === 'ok' ? 'bg-emerald-50 border-emerald-500 text-emerald-800' :
                  'bg-zinc-50 border-zinc-400 text-zinc-700'
                }`}
              >
                {hint.text}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
